import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { GraduationCap, Plus, Coins, MessageSquare, Search, X, LogOut, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api, logout, setStoredUser } from '@/utils/api';
import { trackContact, trackLead } from '@/lib/metaPixel';

const tricityLocations = [
  // Chandigarh (grouped)
  'Chandigarh - Sector 1-10', 'Chandigarh - Sector 11-20', 'Chandigarh - Sector 21-30',
  'Chandigarh - Sector 31-40', 'Chandigarh - Sector 41-50', 'Chandigarh - Sector 51-56',
  'Chandigarh - Manimajra', 'Chandigarh - Dhanas', 'Chandigarh - Maloya', 'Chandigarh - IT Park',
  'Chandigarh - PGI Area', 'Chandigarh - Industrial Area',
  // Mohali
  'Mohali - Phase 1-5', 'Mohali - Phase 6-11', 'Mohali - Sector 58-70', 'Mohali - Sector 71-80',
  'Mohali - Sector 81-91', 'Mohali - Aerocity', 'Mohali - IT City', 'Mohali - Kharar Road',
  'Mohali - Airport Road', 'Mohali - VR Punjab', 'Mohali - Balongi', 'Mohali - Sohana',
  // Panchkula
  'Panchkula - Sector 1-10', 'Panchkula - Sector 11-20', 'Panchkula - Sector 21-28',
  'Panchkula - MDC', 'Panchkula - Pinjore', 'Panchkula - Kalka', 'Panchkula - Barwala',
  // Zirakpur
  'Zirakpur - VIP Road', 'Zirakpur - Baltana', 'Zirakpur - Pabhat', 'Zirakpur - Dhakoli',
  'Zirakpur - Peer Muchalla', 'Zirakpur - Gazipur', 'Zirakpur - NAC',
  // Nearby Areas
  'Kharar', 'Kharar - Landran', 'Derabassi', 'Mullanpur', 'New Chandigarh',
  'Nayagaon', 'Kurali', 'Morinda', 'Gharuan', 'Lalru', 'Rajpura'
];

export default function StudentDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [myRequirements, setMyRequirements] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPostModal, setShowPostModal] = useState(false);
  const [postRequirementError, setPostRequirementError] = useState('');
  const [currentUser, setCurrentUser] = useState(user);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paidTutors, setPaidTutors] = useState(() => {
    // Restore paidTutors from sessionStorage
    const saved = sessionStorage.getItem('paidTutors');
    return saved ? JSON.parse(saved) : {};
  });
  const [activeTab, setActiveTab] = useState(() => {
    // Restore tab from sessionStorage or default to 'requirements'
    return sessionStorage.getItem('studentDashboardTab') || 'requirements';
  });
  const [tutorFilters, setTutorFilters] = useState({
    subject: '',
    location: '',
    minFee: '',
    maxFee: ''
  });
  const [requirementData, setRequirementData] = useState({
    subject: '',
    level_class: '',
    mode: [],  // Changed to array for multiple selections
    requirement_type: 'Tuition',
    gender_preference: 'Any',
    time_preference: 'Part Time',
    languages: ['English'],
    board: '',
    budget_min: '',
    budget_max: '',
    location: '',
    phone: '',
    description: ''
  });
  const isOnlineOnlyRequirement =
    requirementData.mode.length > 0 &&
    requirementData.mode.every((mode) => String(mode).trim().toLowerCase() === 'online');
  const isLocationRequired = requirementData.mode.length > 0 && !isOnlineOnlyRequirement;

  useEffect(() => {
    loadMyRequirements();
    loadTutors();
    loadUnreadCount();
    loadCurrentUser();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('open') === 'post') {
      setShowPostModal(true);
      setActiveTab('requirements');
      sessionStorage.setItem('studentDashboardTab', 'requirements');
      params.delete('open');
      const next = params.toString();
      navigate(`/student/dashboard${next ? `?${next}` : ''}`, { replace: true });
    }
  }, [location.search, navigate]);

  // Check access for all tutors when tutors are loaded
  useEffect(() => {
    if (tutors.length > 0) {
      checkAllTutorAccess();
    }
  }, [tutors]);

  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/me');
      setCurrentUser(response.data);
      const verifiedMobile = String(response.data?.mobile || '').replace(/\D/g, '');
      if (verifiedMobile.length === 10) {
        setRequirementData((prev) => ({ ...prev, phone: verifiedMobile }));
      }
    } catch (error) {
      console.error('Failed to load user info:', error);
    }
  };

  const checkAllTutorAccess = async () => {
    const accessMap = {};
    for (const tutor of tutors) {
      try {
        const response = await api.get(`/check-tutor-access/${tutor.user_id}`);
        accessMap[tutor.user_id] = response.data;
      } catch (error) {
        accessMap[tutor.user_id] = { has_contact_access: false, has_message_access: false };
      }
    }
    setPaidTutors(accessMap);
    sessionStorage.setItem('paidTutors', JSON.stringify(accessMap));
  };

  const handleMessageTutor = (tutorId, tutorName) => {
    navigate('/messages', { state: { recipientId: tutorId, recipientName: tutorName } });
  };

  const loadMyRequirements = async () => {
    try {
      const response = await api.get('/requirements/my');
      setMyRequirements(response.data);
    } catch (error) {
      console.error('Failed to load requirements:', error);
    }
  };

  const loadTutors = async (filters = tutorFilters) => {
    try {
      const params = new URLSearchParams();
      if (filters.subject) params.append('subject', filters.subject);
      if (filters.location) params.append('location', filters.location);
      if (filters.minFee) params.append('min_fee', filters.minFee);
      if (filters.maxFee) params.append('max_fee', filters.maxFee);

      const query = params.toString();
      const response = await api.get(`/tutors${query ? `?${query}` : ''}`);
      setTutors(response.data);
    } catch (error) {
      console.error('Failed to load tutors:', error);
    }
  };

  const handleTutorSearch = () => {
    loadTutors(tutorFilters);
  };

  const loadUnreadCount = async () => {
    try {
      const response = await api.get('/messages/unread');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handlePostRequirement = async (e) => {
    e.preventDefault();
    setPostRequirementError('');

    const enteredPhone = String(requirementData.phone || '').replace(/\D/g, '');
    if (enteredPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    await submitRequirement();
  };

  const submitRequirement = async () => {
    try {
      const payload = {
        ...requirementData,
        location: requirementData.location?.trim() || null,
        board: requirementData.board || null,
        budget_min: requirementData.budget_min !== '' ? Number(requirementData.budget_min) : null,
        budget_max: requirementData.budget_max !== '' ? Number(requirementData.budget_max) : null
      };
      if (
        payload.budget_min !== null &&
        payload.budget_max !== null &&
        payload.budget_max < payload.budget_min
      ) {
        setPostRequirementError('Maximum budget must be greater than or equal to minimum budget.');
        return;
      }
      await api.post('/requirements', payload);
      toast.success('Requirement posted successfully!');
      trackLead({
        content_name: 'student_requirement',
        content_category: payload.requirement_type || 'Tuition',
        subject: payload.subject,
        level_class: payload.level_class,
        location: payload.location || 'online',
        preferred_modes: payload.mode,
        preferred_languages: payload.languages,
      }, {
        email: currentUser?.email || user?.email,
        mobile: payload.phone,
        external_id: currentUser?.id || user?.id,
      });
      setShowPostModal(false);
      setPostRequirementError('');
      setRequirementData({
        subject: '',
        level_class: '',
        mode: [],
        requirement_type: 'Tuition',
        gender_preference: 'Any',
        time_preference: 'Part Time',
        languages: ['English'],
        board: '',
        budget_min: '',
        budget_max: '',
        location: '',
        phone: '',
        description: ''
      });
      loadMyRequirements();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to post requirement';
      setPostRequirementError(detail);
      toast.error(detail);
    }
  };

  const handleDeleteRequirement = async (reqId) => {
    try {
      await api.delete(`/requirements/${reqId}`);
      toast.success('Requirement closed');
      loadMyRequirements();
    } catch (error) {
      toast.error('Failed to close requirement');
    }
  };

  const handleDeleteProfile = async () => {
    try {
      await api.delete('/profile/delete');
      toast.success('Profile deleted successfully');
      logout();
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const handleModeChange = (mode, checked) => {
    if (checked) {
      setRequirementData({ ...requirementData, mode: [...requirementData.mode, mode] });
    } else {
      setRequirementData({ ...requirementData, mode: requirementData.mode.filter(m => m !== mode) });
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    sessionStorage.setItem('studentDashboardTab', value);
  };

  const handleContactTutor = async (tutorId) => {
    const availableCoins = currentUser?.coins ?? user?.coins ?? 0;
    if (availableCoins < 100) {
      toast.error('Insufficient coins. You need 100 coins to contact a tutor.');
      return;
    }
    
    try {
      const response = await api.post('/wallet/spend', null, {
        params: { coins: 100, purpose: 'contact_tutor', target_id: tutorId }
      });
      
      toast.success('Contact & messaging unlocked! You can now message this tutor.');
      trackContact({
        content_name: 'student_contact_tutor',
        content_category: 'Tutor Contact Unlock',
        content_ids: [tutorId],
        value: 100,
        currency: 'INR',
      }, {
        email: currentUser?.email || user?.email,
        mobile: currentUser?.mobile || user?.mobile,
        external_id: currentUser?.id || user?.id,
      });
      
      const newCoins = response.data.remaining_coins;
      const updatedUser = { ...(user || {}), coins: newCoins };
      if (setUser) setUser(updatedUser);
      setStoredUser(updatedUser);
      
      // Update paidTutors state and persist to sessionStorage
      const updatedPaidTutors = {
        ...paidTutors,
        [tutorId]: { has_contact_access: true, has_message_access: true }
      };
      setPaidTutors(updatedPaidTutors);
      sessionStorage.setItem('paidTutors', JSON.stringify(updatedPaidTutors));
      
      // Force re-render by updating a local state
      setCurrentUser(prev => ({ ...(prev || {}), coins: newCoins }));
      
      if (response.data.data) {
        toast.info(`Mobile: ${response.data.data.mobile || 'Not provided'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock contact');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-orange-500" />
            <span className="text-2xl font-outfit font-bold">Tricity Tutors</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/wallet">
              <Button data-testid="student-wallet-btn" variant="outline" className="rounded-full flex items-center gap-2">
                <Coins className="w-4 h-4 text-yellow-600" />
                <span className="font-semibold">{currentUser?.coins ?? user?.coins ?? 0}</span>
              </Button>
            </Link>
            <Link to="/messages">
              <Button data-testid="student-messages-btn" variant="outline" className="rounded-full relative">
                <MessageSquare className="w-4 h-4" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-500 text-xs">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>
            <Button data-testid="student-logout-btn" onClick={logout} variant="outline" className="rounded-full">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-6">
          <div>
            <h1 className="text-4xl font-outfit font-bold mb-2">Welcome, {user?.name}</h1>
            <p className="text-muted-foreground">Find the perfect tutor for your needs</p>
          </div>
        </div>
        <div className="mb-8">
          <div className="mt-0 flex flex-wrap items-center justify-between gap-2">
            <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
            <DialogTrigger asChild>
              <Button data-testid="post-requirement-btn" className="bg-orange-500 hover:bg-orange-600 rounded-full flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Post Requirement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Post Your Requirement</DialogTitle>
                <DialogDescription>
                  Fill in the details to find the perfect tutor
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handlePostRequirement} className="space-y-4">
                {postRequirementError && (
                  <div className="p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
                    {postRequirementError}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Subject</Label>
                    <Input
                      data-testid="req-subject-input"
                      value={requirementData.subject}
                      onChange={(e) => setRequirementData({ ...requirementData, subject: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Level/Class</Label>
                    <Input
                      data-testid="req-level-input"
                      value={requirementData.level_class}
                      onChange={(e) => setRequirementData({ ...requirementData, level_class: e.target.value })}
                      placeholder="e.g., Class 10, B.Tech"
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Study Mode * (Select all that apply)</Label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="mode-online"
                          checked={requirementData.mode.includes('Online')}
                          onCheckedChange={(checked) => handleModeChange('Online', checked)}
                        />
                        <label htmlFor="mode-online" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Online Classes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="mode-home"
                          checked={requirementData.mode.includes('Home')}
                          onCheckedChange={(checked) => handleModeChange('Home', checked)}
                        />
                        <label htmlFor="mode-home" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          Home Tuition (Tutor visits me)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="mode-travel"
                          checked={requirementData.mode.includes('I can travel')}
                          onCheckedChange={(checked) => handleModeChange('I can travel', checked)}
                        />
                        <label htmlFor="mode-travel" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          I can travel to tutor
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="mode-institute"
                          checked={requirementData.mode.includes('Institute')}
                          onCheckedChange={(checked) => handleModeChange('Institute', checked)}
                        />
                        <label htmlFor="mode-institute" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                          At institute or coaching centre
                        </label>
                      </div>
                    </div>
                    {requirementData.mode.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">Please select at least one mode</p>
                    )}
                  </div>
                  <div>
                    <Label>Requirement Type</Label>
                    <Select value={requirementData.requirement_type} onValueChange={(value) => setRequirementData({ ...requirementData, requirement_type: value })}>
                      <SelectTrigger data-testid="req-type-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tuition">Tuition</SelectItem>
                        <SelectItem value="Assignment Help">Assignment Help</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Gender Preference</Label>
                    <Select value={requirementData.gender_preference} onValueChange={(value) => setRequirementData({ ...requirementData, gender_preference: value })}>
                      <SelectTrigger data-testid="req-gender-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Any">Any</SelectItem>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Board</Label>
                    <Select value={requirementData.board || ''} onValueChange={(value) => setRequirementData({ ...requirementData, board: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select board (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CBSE">CBSE</SelectItem>
                        <SelectItem value="ICSE">ICSE</SelectItem>
                        <SelectItem value="State Board">State Board</SelectItem>
                        <SelectItem value="IB">IB</SelectItem>
                        <SelectItem value="IGCSE">IGCSE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Time Preference</Label>
                    <Select value={requirementData.time_preference} onValueChange={(value) => setRequirementData({ ...requirementData, time_preference: value })}>
                      <SelectTrigger data-testid="req-time-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Part Time">Part Time</SelectItem>
                        <SelectItem value="Full Time">Full Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Min Budget (₹/hr)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={requirementData.budget_min}
                      onChange={(e) => setRequirementData({ ...requirementData, budget_min: e.target.value })}
                      placeholder="e.g., 200"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Max Budget (₹/hr)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={requirementData.budget_max}
                      onChange={(e) => setRequirementData({ ...requirementData, budget_max: e.target.value })}
                      placeholder="e.g., 500"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Location {isLocationRequired ? '*' : '(Optional for Online)'}</Label>
                    <select
                      data-testid="req-location-select"
                      value={requirementData.location}
                      onChange={(e) => setRequirementData({ ...requirementData, location: e.target.value })}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                      required={isLocationRequired}
                    >
                      <option value="">Select Location</option>
                      {tricityLocations.map((loc, idx) => (
                        <option key={idx} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      data-testid="req-phone-input"
                      type="tel"
                      value={requirementData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setRequirementData({ ...requirementData, phone: value });
                        }
                      }}
                      placeholder="10-digit mobile number"
                      required
                      maxLength={10}
                      pattern="[0-9]{10}"
                      className="mt-1"
                    />
                    {requirementData.phone && requirementData.phone.length !== 10 && (
                      <p className="text-xs text-red-500 mt-1">Mobile number must be exactly 10 digits</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Languages (comma separated)</Label>
                  <Input
                    data-testid="req-languages-input"
                    value={requirementData.languages.join(', ')}
                    onChange={(e) => setRequirementData({ ...requirementData, languages: e.target.value.split(',').map(l => l.trim()) })}
                    placeholder="English, Hindi, Punjabi"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    data-testid="req-description-input"
                    value={requirementData.description}
                    onChange={(e) => setRequirementData({ ...requirementData, description: e.target.value })}
                    placeholder="Additional details about your requirement"
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <Button
                  data-testid="submit-requirement-btn"
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 rounded-full"
                  disabled={!requirementData.phone || requirementData.phone.length !== 10}
                >
                  Post Requirement
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={activeTab === 'requirements' ? 'default' : 'outline'}
                className="rounded-full border"
                onClick={() => handleTabChange('requirements')}
              >
                My Requirements
              </Button>
              <Button
                type="button"
                variant={activeTab === 'browse' ? 'default' : 'outline'}
                className="rounded-full border"
                onClick={() => handleTabChange('browse')}
              >
                Browse Tutors
              </Button>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8">

          {/* My Requirements Tab */}
          <TabsContent value="requirements" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Posted Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {myRequirements.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground mb-4">You haven't posted any requirements yet</p>
                      <Button data-testid="post-first-requirement-btn" onClick={() => setShowPostModal(true)} className="bg-orange-500 hover:bg-orange-600 rounded-full">
                        Post Your First Requirement
                      </Button>
                    </div>
                  ) : (
                    myRequirements.map((req) => (
                      <div key={req.id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-lg">{req.subject}</h3>
                            <p className="text-sm text-muted-foreground">
                              {req.level_class} • {Array.isArray(req.mode) ? req.mode.join(', ') : req.mode}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={req.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                              {req.status}
                            </Badge>
                            {req.status === 'active' && (
                              <Button
                                data-testid={`delete-requirement-${req.id}-btn`}
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteRequirement(req.id)}
                                className="rounded-full"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm mb-2">{req.description}</p>
                        <div className="flex justify-between items-center text-sm text-muted-foreground">
                          <span>{req.location}</span>
                          <span>{new Date(req.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Browse Tutors Tab */}
          <TabsContent value="browse" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-outfit font-bold">Available Tutors</h2>
              <Link to="/tutors">
                <Button data-testid="view-all-tutors-btn" variant="outline" className="rounded-full flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  View All Tutors
                </Button>
              </Link>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Search Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    data-testid="dashboard-search-subject-input"
                    placeholder="Subject (e.g., Math, Physics)"
                    value={tutorFilters.subject}
                    onChange={(e) => setTutorFilters({ ...tutorFilters, subject: e.target.value })}
                  />
                  <Input
                    data-testid="dashboard-search-location-input"
                    placeholder="Location (e.g., Chandigarh)"
                    value={tutorFilters.location}
                    onChange={(e) => setTutorFilters({ ...tutorFilters, location: e.target.value })}
                  />
                  <Input
                    data-testid="dashboard-search-min-fee-input"
                    type="number"
                    placeholder="Min Fee"
                    value={tutorFilters.minFee}
                    onChange={(e) => setTutorFilters({ ...tutorFilters, minFee: e.target.value })}
                  />
                  <Input
                    data-testid="dashboard-search-max-fee-input"
                    type="number"
                    placeholder="Max Fee"
                    value={tutorFilters.maxFee}
                    onChange={(e) => setTutorFilters({ ...tutorFilters, maxFee: e.target.value })}
                  />
                </div>
                <Button
                  data-testid="dashboard-search-tutors-btn"
                  onClick={handleTutorSearch}
                  className="mt-4 rounded-full"
                  size="lg"
                >
                  Search Tutors
                </Button>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tutors.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-muted-foreground">No tutors found. Try adjusting your filters.</p>
                </div>
              ) : tutors.map((tutor) => {
                const hasPaid = paidTutors[tutor.user_id]?.has_contact_access;
                return (
                <Card key={tutor.user_id} className="card-hover">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{tutor.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{tutor.location}</p>
                      </div>
                      <Badge className="bg-yellow-500">{tutor.average_rating?.toFixed(1) || '0.0'} ⭐</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {tutor.subjects && tutor.subjects.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Subject Expertise:</span>
                          <div className="mt-2 space-y-2">
                            {tutor.subjects.slice(0, 3).map((sub, idx) => (
                              <div key={idx} className="rounded-md border p-2">
                                <p className="font-medium">{sub.subject}</p>
                                {sub.classes && sub.classes.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {sub.classes.map((cls, i) => (
                                      <Badge key={i} variant="secondary" className="text-xs">{cls}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                            {tutor.subjects.length > 3 && (
                              <p className="text-xs text-muted-foreground">+{tutor.subjects.length - 3} more subjects</p>
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Teaching Modes:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {tutor.teaches_online && <Badge variant="outline" className="text-xs">Online</Badge>}
                          {tutor.teaches_at_home && <Badge variant="outline" className="text-xs">Tutor Home</Badge>}
                          {tutor.can_travel && <Badge variant="outline" className="text-xs">Student Home</Badge>}
                          {!tutor.teaches_online && !tutor.teaches_at_home && !tutor.can_travel && (
                            <span className="text-xs text-muted-foreground">Not specified</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Fee:</span>
                        <span className="ml-2 font-semibold">₹{tutor.fee_min} - ₹{tutor.fee_max}/hr</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs">
                          Reviews: {(tutor.reviews_count ?? tutor.reviews?.length) || 0}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Profile: {tutor.profile_completeness ?? 0}%
                        </Badge>
                        <Badge variant={tutor.contact_verified ? 'default' : 'outline'} className="text-xs">
                          {tutor.contact_verified ? 'Verified Contact' : 'Contact Unverified'}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Languages:</span>
                        <span className="ml-2">{tutor.languages?.join(', ') || 'Not specified'}</span>
                      </div>
                      <div className="flex flex-col gap-2 mt-4">
                        <div className="flex gap-2">
                          <Link to={`/tutors/${tutor.user_id}`} className="flex-1">
                            <Button
                              data-testid={`view-tutor-${tutor.user_id}-btn`}
                              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                              size="sm"
                            >
                              View Profile
                            </Button>
                          </Link>
                          {hasPaid ? (
                            <Button
                              data-testid={`contact-unlocked-${tutor.user_id}-btn`}
                              size="sm"
                              variant="outline"
                              className="flex-1 rounded-full border-green-500 text-green-600"
                              onClick={() => toast.info(`Mobile: ${tutor.mobile || 'Not provided'}`)}
                            >
                              ✓ Contact Unlocked
                            </Button>
                          ) : (
                            <Button
                              data-testid={`contact-tutor-${tutor.user_id}-btn`}
                              size="sm"
                              onClick={() => handleContactTutor(tutor.user_id)}
                              className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
                            >
                              Contact (100 coins)
                            </Button>
                          )}
                        </div>
                        {hasPaid && (
                          <Button
                            data-testid={`message-tutor-${tutor.user_id}-btn`}
                            size="sm"
                            onClick={() => handleMessageTutor(tutor.user_id, tutor.name)}
                            className="w-full rounded-full bg-indigo-500 hover:bg-indigo-600"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send Message
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          </TabsContent>
        </Tabs>

        {/* Delete Profile Section */}
        <Card className="mt-8 border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Once you delete your profile, all your data will be permanently removed. This action cannot be undone.
            </p>
            <Button 
              variant="outline" 
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete My Profile
            </Button>
          </CardContent>
        </Card>

        {/* Delete Profile Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Profile
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete your profile? This action cannot be undone.
                All your requirements, messages, and profile information will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                className="flex-1"
                onClick={handleDeleteProfile}
              >
                Delete Permanently
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
